import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { CaretLeft, CaretRight, MapPin, Clock, CalendarBlank, MagnifyingGlass, Wrench, Package } from '@phosphor-icons/react';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  isSameDay,
  parseISO,
  isValid,
} from 'date-fns';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { API, ORDERS_LIST_PARAMS } from '../config';
import { orderMatchesGlobalSearch } from '../utils/listSearch';
import {
  getPlanningOrderViewPath,
  isOrderAwaitingServicePlanning,
  isOrderInActiveServiceQueue,
} from '../utils/orderAttention';
import {
  defaultSidebarDuration,
  formatPlanningDurationLabel,
  getPlanningWeekdaySpan,
  weekdaySpanFromDuration,
} from '../utils/planningDuration';

// Custom portal for dragging - this fixes the alignment issue.
// Only portal while actively dragging; keep a stable tree when idle (avoids insertBefore
// crashes when Orders/Service tabs remount Droppables under React Strict Mode).
const PortalAwareItem = ({ provided, snapshot, children }) => {
  const child = (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={provided.draggableProps.style}
    >
      {children}
    </div>
  );

  if (snapshot.isDragging && typeof document !== 'undefined' && document.body) {
    return createPortal(child, document.body);
  }
  return child;
};

const parsePlanningDragOrderId = (draggableId) => {
  const raw = String(draggableId);
  return raw.includes('__') ? raw.split('__')[0] : raw;
};

const planningDragId = (orderId, day) =>
  `${orderId}__${format(day, 'yyyy-MM-dd')}`;

const isSidebarDroppableId = (droppableId) => {
  const id = String(droppableId || '');
  return id === 'unassigned' || id === 'sidebar-orders' || id === 'sidebar-service';
};

function PlanningDurationControls({ orderId, duration, onChange, t, testIdPrefix }) {
  const unit = duration?.unit || 'days';
  const value = duration?.value ?? 1;
  const showValueInput = unit !== 'half_day';
  return (
    <div
      className="mt-2 space-y-1.5"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-2 text-[10px] text-[#64748b]">
        <span>{t('planningSidebarDuration')}</span>
        <select
          value={unit}
          onChange={(e) => onChange(orderId, { unit: e.target.value, value: e.target.value === 'half_day' ? 1 : value })}
          className="vmk-input !w-[5.5rem] !py-1 !text-xs !px-1"
          data-testid={`${testIdPrefix}-unit-${orderId}`}
        >
          <option value="days">{t('planningDurationUnitDays')}</option>
          <option value="half_day">{t('planningDurationUnitHalfDay')}</option>
          <option value="hours">{t('planningDurationUnitHours')}</option>
          <option value="minutes">{t('planningDurationUnitMinutes')}</option>
        </select>
      </div>
      {showValueInput ? (
        <div className="flex items-center justify-end">
          <input
            type="number"
            min={unit === 'days' || unit === 'hours' ? '0.5' : '1'}
            step={unit === 'minutes' ? '1' : '0.5'}
            value={value}
            onChange={(e) => onChange(orderId, { value: Number(e.target.value) })}
            className="vmk-input !w-16 !py-1 !text-xs"
            data-testid={`${testIdPrefix}-value-${orderId}`}
          />
        </div>
      ) : null}
    </div>
  );
}

const PlanningPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [viewMode, setViewMode] = useState('week');
  const [allOrders, setAllOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarDuration, setSidebarDuration] = useState({});
  const [planningMode, setPlanningMode] = useState('orders'); // 'orders' | 'service'

  const montageSidebarOrders = useMemo(
    () => allOrders.filter((o) => o.status === 'unassigned'),
    [allOrders],
  );
  const serviceSidebarOrders = useMemo(
    () => allOrders.filter((o) => isOrderAwaitingServicePlanning(o)),
    [allOrders],
  );
  const sidebarOrders = planningMode === 'service' ? serviceSidebarOrders : montageSidebarOrders;

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i)), [currentWeek]);
  const monthDays = useMemo(
    () => eachDayOfInterval({ start: startOfMonth(currentWeek), end: endOfMonth(currentWeek) }),
    [currentWeek]
  );
  const monthGridDays = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(currentWeek), { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(currentWeek), { weekStartsOn: 1 }),
      }),
    [currentWeek]
  );
  const visibleDays = viewMode === 'month' ? monthDays : weekDays;
  const periodStart = viewMode === 'month' ? startOfMonth(currentWeek) : currentWeek;
  const periodEnd = viewMode === 'month' ? endOfMonth(currentWeek) : endOfWeek(currentWeek, { weekStartsOn: 1 });
  const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const markerStyles = {
    red: { bg: 'rgba(239, 68, 68, 0.16)', border: '#ef4444' },
    yellow: { bg: 'rgba(245, 158, 11, 0.2)', border: '#f59e0b' },
    green: { bg: 'rgba(16, 185, 129, 0.18)', border: '#10b981' },
  };

  const isWeekend = (d) => {
    const n = d.getDay();
    return n === 0 || n === 6;
  };

  const addWorkingDays = (startDate, daysToInclude) => {
    if (!isValid(startDate)) return startDate;
    let cursor = startDate;
    if (isWeekend(cursor)) {
      while (isWeekend(cursor)) cursor = addDays(cursor, 1);
    }
    let remaining = Math.max(daysToInclude, 1) - 1;
    while (remaining > 0) {
      cursor = addDays(cursor, 1);
      if (isWeekend(cursor)) continue;
      remaining -= 1;
    }
    return cursor;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getOrderActiveRange = (order) => {
    const startRaw = order.scheduled_date || order.montagedatum;
    if (!startRaw) return null;
    let start = parseISO(startRaw);
    if (!isValid(start)) return null;
    if (isWeekend(start)) {
      while (isWeekend(start)) start = addDays(start, 1);
    }
    const totalDays = getPlanningWeekdaySpan(order);
    const plannedEnd = addWorkingDays(start, totalDays);
    const completedRaw =
      order.completed_at ||
      order.mobile_completed_at ||
      order.client_signature_json?.signed_at ||
      null;
    const completedAt = completedRaw ? parseISO(String(completedRaw)) : null;
    const today = parseISO(format(new Date(), 'yyyy-MM-dd'));
    const end = order.mobile_work_status === 'completed'
      ? (
          (completedAt && isValid(completedAt) && completedAt < plannedEnd)
            ? completedAt
            : today < plannedEnd
              ? today
              : plannedEnd
        )
      : plannedEnd;
    return { start, end };
  };

  const plannedOrders = useMemo(() => {
    const periodStartDay = parseISO(format(periodStart, 'yyyy-MM-dd'));
    const periodEndDay = parseISO(format(periodEnd, 'yyyy-MM-dd'));
    if (!isValid(periodStartDay) || !isValid(periodEndDay)) return [];

    return allOrders.filter((o) => {
      if (String(o.status || '') !== 'assigned') return false;
      if (String(o.mobile_work_status || '') === 'completed') return false;
      const isService = isOrderInActiveServiceQueue(o);
      if (planningMode === 'service' ? !isService : isService) return false;
      const range = getOrderActiveRange(o);
      if (!range) return false;
      return range.start <= periodEndDay && range.end >= periodStartDay;
    });
  }, [allOrders, periodStart, periodEnd, planningMode]);

  useEffect(() => {
    setSidebarDuration((prev) => {
      const next = { ...prev };
      const seed = [...montageSidebarOrders, ...serviceSidebarOrders, ...allOrders];
      for (const order of seed) {
        const id = String(order.id);
        if (next[id] == null) {
          next[id] = defaultSidebarDuration(order);
        }
      }
      return next;
    });
  }, [allOrders, montageSidebarOrders, serviceSidebarOrders]);

  const getSidebarDuration = (orderId) => {
    const id = String(orderId);
    const stored = sidebarDuration[id];
    if (stored && stored.unit && Number(stored.value) > 0) return stored;
    return { value: 1, unit: 'days' };
  };

  const setSidebarDurationForOrder = (orderId, patch) => {
    setSidebarDuration((prev) => {
      const id = String(orderId);
      const current = prev[id] || { value: 1, unit: 'days' };
      const next = { ...current, ...patch };
      if (next.unit === 'half_day') {
        next.value = Math.max(Number(next.value) || 1, 1);
      } else if (!(Number(next.value) > 0)) {
        next.value = 1;
      }
      return { ...prev, [id]: next };
    });
  };

  const fetchData = async () => {
    try {
      const [teamsRes, allOrdersRes] = await Promise.all([
        axios.get(`${API}/teams`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true, ...ORDERS_LIST_PARAMS }),
      ]);

      setTeams(teamsRes.data);
      setAllOrders(Array.isArray(allOrdersRes.data) ? allOrdersRes.data : []);
    } catch (error) {
      toast.error(t('failedFetchData'));
    } finally {
      setLoading(false);
    }
  };

  const getOrdersForDay = (date) => {
    const dayStart = parseISO(format(date, 'yyyy-MM-dd'));
    if (isWeekend(dayStart)) return [];
    return plannedOrders.filter((order) => {
      const range = getOrderActiveRange(order);
      if (!range) return false;
      if (activeSearchIds && !activeSearchIds.has(String(order.id))) return false;
      return dayStart >= range.start && dayStart <= range.end;
    });
  };

  const getOrderMarkerStyle = (order) => {
    const key = String(order?.planning_marker_color || '').trim().toLowerCase();
    return markerStyles[key] || null;
  };

  const countWorkingDays = (startDate, endDate) => {
    if (!isValid(startDate) || !isValid(endDate) || endDate < startDate) return 0;
    let count = 0;
    for (let d = startDate; d <= endDate; d = addDays(d, 1)) {
      if (!isWeekend(d)) count += 1;
    }
    return count;
  };

  const searchNeedle = searchQuery.trim().toLowerCase();
  const searchMatches = useMemo(() => {
    if (!searchNeedle) return [];
    return plannedOrders
      .map((order) => {
        if (!orderMatchesGlobalSearch(order, searchNeedle)) return null;
        const range = getOrderActiveRange(order);
        if (!range) return null;
        const overlapStart = range.start > periodStart ? range.start : periodStart;
        const overlapEnd = range.end < periodEnd ? range.end : periodEnd;
        const workDays = countWorkingDays(overlapStart, overlapEnd);
        if (workDays <= 0) return null;
        return { order, workDays };
      })
      .filter(Boolean);
  }, [plannedOrders, searchNeedle, periodStart, periodEnd]);

  const activeSearchIds = useMemo(() => {
    if (!searchNeedle) return null;
    return new Set(searchMatches.map((m) => String(m.order.id)));
  }, [searchNeedle, searchMatches]);

  const planningOrdersTotal = useMemo(() => {
    const seen = new Set();
    let sum = 0;
    let missing = 0;
    for (const o of plannedOrders) {
      const id = String(o.id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const raw = o.offer_total_amount;
      if (raw == null || raw === '') {
        missing += 1;
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n)) missing += 1;
      else sum += n;
    }
    return { sum, missing };
  }, [plannedOrders]);

  const formatPlanningTotal = (value) =>
    new Intl.NumberFormat(language === 'nl' ? 'nl-NL' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;

    const orderId = parsePlanningDragOrderId(draggableId);
    const draggedOrder = allOrders.find((o) => String(o.id) === String(orderId));

    if (isSidebarDroppableId(destination.droppableId)) {
      try {
        if (draggedOrder && isOrderInActiveServiceQueue(draggedOrder)) {
          await axios.put(`${API}/orders/${orderId}/clear-planning`, {}, { withCredentials: true });
          toast.success(t('planningServiceClearedForReschedule'));
        } else {
          await axios.put(`${API}/orders/${orderId}/unassign`, {}, { withCredentials: true });
          toast.success(t('orderUnassignedSuccess'));
        }
        fetchData();
      } catch (error) {
        toast.error(t('failedUnassignOrder'));
      }
      return;
    }

    const parts = destination.droppableId.split('-team-');
    const dateStr = parts[0].replace('day-', '');
    let teamId = parts[1];

    if (!teamId) {
      toast.error(t('planningDragTeamRequired'));
      return;
    }

    if (teamId === 'self') {
      if (!draggedOrder?.assigned_team_id) {
        toast.error(t('planningDragTeamRequired'));
        return;
      }
      teamId = String(draggedOrder.assigned_team_id);
    }

    const fromSidebar = isSidebarDroppableId(source.droppableId);
    const payload = { team_id: teamId, scheduled_date: dateStr };
    if (fromSidebar) {
      const dur = getSidebarDuration(orderId);
      payload.planning_duration_value = Number(dur.value);
      payload.planning_duration_unit = dur.unit;
      payload.montagedagen = weekdaySpanFromDuration(dur.value, dur.unit);
    } else if (draggedOrder) {
      const unit = String(draggedOrder.planning_duration_unit || '').trim().toLowerCase();
      const value = Number(draggedOrder.planning_duration_value);
      if (unit && Number.isFinite(value) && value > 0) {
        payload.planning_duration_value = value;
        payload.planning_duration_unit = unit;
        payload.montagedagen = getPlanningWeekdaySpan(draggedOrder);
      } else {
        payload.montagedagen = Math.max(Number(draggedOrder.montagedagen || 0), 1);
      }
    }

    try {
      await axios.put(
        `${API}/orders/${orderId}/assign`,
        payload,
        { withCredentials: true }
      );
      toast.success(t('orderAssignedSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('failedAssignOrder'));
    }
  };

  const prevWeek = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const nextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));
  const prevMonth = () => setCurrentWeek(startOfWeek(subMonths(currentWeek, 1), { weekStartsOn: 1 }));
  const nextMonth = () => setCurrentWeek(startOfWeek(addMonths(currentWeek, 1), { weekStartsOn: 1 }));
  const goToToday = () => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const isServiceMode = planningMode === 'service';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00CBA9] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div
      data-testid="planning-page"
      className={`animate-fadeIn h-full ${
        isServiceMode
          ? 'rounded-xl border-2 border-amber-400/70 bg-amber-50/40 p-3 dark:border-amber-600/50 dark:bg-amber-950/20'
          : 'rounded-xl border-2 border-[#00CBA9]/35 bg-[rgba(0,203,169,0.04)] p-3 dark:border-[#00CBA9]/30 dark:bg-[#00CBA9]/10'
      }`}
    >
      {/* Header */}
      <div className="mb-4 grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_minmax(360px,520px)_1fr]">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="page-title">{t('planning')}</h1>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                isServiceMode
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'bg-[#00CBA9] text-white shadow-sm'
              }`}
              data-testid="planning-mode-badge"
            >
              {isServiceMode ? <Wrench size={14} weight="bold" /> : <Package size={14} weight="bold" />}
              {isServiceMode ? t('planningServiceTab') : t('planningOrdersTab')}
            </span>
          </div>
          <p className="page-subtitle">
            {viewMode === 'month'
              ? `${format(startOfMonth(currentWeek), 'MMMM yyyy')}`
              : `${t('week')} ${format(currentWeek, 'w')} • ${format(currentWeek, 'MMMM yyyy')}`}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
              {viewMode === 'month' ? t('planningMonthTotalLabel') : t('planningWeekTotalLabel')}
            </span>
            <span
              className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-bold shadow-sm ${
                isServiceMode
                  ? 'border-amber-400/50 bg-gradient-to-r from-amber-200/60 to-orange-200/50 text-amber-900 dark:border-amber-500/40 dark:from-amber-900/40 dark:to-orange-900/30 dark:text-amber-200'
                  : 'border-[#00CBA9]/30 bg-gradient-to-r from-[#00CBA9]/20 to-[#34d399]/20 text-[#047857] dark:border-[#00CBA9]/40 dark:from-[#00CBA9]/25 dark:to-[#34d399]/25 dark:text-[#6ee7b7]'
              }`}
            >
              {formatPlanningTotal(planningOrdersTotal.sum)}
            </span>
            {planningOrdersTotal.missing > 0 ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                {t('planningTotalExcludesMissing')}
              </span>
            ) : null}
          </div>
        </div>
        <div className="w-full justify-self-center lg:self-center">
          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('planningSearchPlaceholder')}
              className="vmk-input !pl-9"
            />
          </div>
          {searchNeedle ? (
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {searchMatches.length > 0 ? (
                searchMatches.map(({ order, workDays }) => {
                  const durLabel = formatPlanningDurationLabel(order, t);
                  return (
                  <span
                    key={order.id}
                    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs ${
                      isServiceMode
                        ? 'border-amber-400/40 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-900/30 dark:text-amber-200'
                        : 'border-[#00CBA9]/30 bg-[#00CBA9]/10 text-[#0f766e] dark:border-[#00CBA9]/40 dark:bg-[#00CBA9]/15 dark:text-[#99f6e4]'
                    }`}
                  >
                    {order.client_name} - {durLabel || `${workDays} ${workDays === 1 ? t('planningWorkday') : t('planningWorkdays')}`}
                  </span>
                  );
                })
              ) : (
                <span className="text-xs text-[#94a3b8]">{t('planningSearchNoResults')}</span>
              )}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2 lg:justify-self-end">
          <button
            onClick={viewMode === 'month' ? prevMonth : prevWeek}
            className="vmk-btn p-3"
            data-testid="prev-week-btn"
          >
            <CaretLeft size={18} weight="bold" />
          </button>
          <button
            onClick={goToToday}
            className="vmk-btn px-5"
            data-testid="today-btn"
          >
            <CalendarBlank size={18} />
            {t('today')}
          </button>
          <button
            onClick={viewMode === 'month' ? nextMonth : nextWeek}
            className="vmk-btn p-3"
            data-testid="next-week-btn"
          >
            <CaretRight size={18} weight="bold" />
          </button>
          <button
            onClick={() => setViewMode((prev) => (prev === 'week' ? 'month' : 'week'))}
            className="vmk-btn px-5"
            data-testid="toggle-view-btn"
          >
            {viewMode === 'week' ? t('month') : t('week')}
          </button>
        </div>
      </div>

      {/* Mode switcher — large segmented control */}
      <div
        className={`mb-3 grid grid-cols-2 gap-1 rounded-xl p-1 ${
          isServiceMode
            ? 'bg-amber-200/70 dark:bg-amber-950/50'
            : 'bg-[#00CBA9]/15 dark:bg-[#0f766e]/30'
        }`}
        role="tablist"
        aria-label={t('planning')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={!isServiceMode}
          className={`flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition-all ${
            !isServiceMode
              ? 'bg-[#00CBA9] text-white shadow-md'
              : 'bg-transparent text-[#64748b] hover:bg-white/50 dark:text-[#94a3b8] dark:hover:bg-white/5'
          }`}
          onClick={() => setPlanningMode('orders')}
          data-testid="planning-tab-orders"
        >
          <Package size={18} weight="bold" />
          {t('planningOrdersTab')} ({montageSidebarOrders.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={isServiceMode}
          className={`flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition-all ${
            isServiceMode
              ? 'bg-amber-500 text-white shadow-md'
              : 'bg-transparent text-[#64748b] hover:bg-white/50 dark:text-[#94a3b8] dark:hover:bg-white/5'
          }`}
          onClick={() => setPlanningMode('service')}
          data-testid="planning-tab-service"
        >
          <Wrench size={18} weight="bold" />
          {t('planningServiceTab')} ({serviceSidebarOrders.length})
        </button>
      </div>

      {/* Explicit mode banner */}
      <div
        className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 ${
          isServiceMode
            ? 'border-amber-400 bg-amber-100 text-amber-950 dark:border-amber-600 dark:bg-amber-950/60 dark:text-amber-100'
            : 'border-[#00CBA9]/40 bg-[#00CBA9]/10 text-[#0f766e] dark:border-[#00CBA9]/40 dark:bg-[#00CBA9]/15 dark:text-[#99f6e4]'
        }`}
        data-testid="planning-mode-banner"
      >
        <div
          className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
            isServiceMode ? 'bg-amber-500 text-white' : 'bg-[#00CBA9] text-white'
          }`}
        >
          {isServiceMode ? <Wrench size={20} weight="bold" /> : <Package size={20} weight="bold" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold leading-tight">
            {isServiceMode ? t('planningModeServiceTitle') : t('planningModeOrdersTitle')}
          </p>
          <p className="mt-0.5 text-xs opacity-90">
            {isServiceMode ? t('planningModeServiceHint') : t('planningModeOrdersHint')}
          </p>
        </div>
      </div>

      <DragDropContext key={planningMode} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 h-[calc(100vh-280px)]">
          {/* Unassigned montage OR service queue awaiting schedule */}
          <div className="w-64 flex-shrink-0">
            <div
              className={`vmk-card-flat h-full flex flex-col overflow-hidden ${
                isServiceMode
                  ? 'ring-2 ring-amber-400 dark:ring-amber-500'
                  : 'ring-2 ring-[#00CBA9]/50 dark:ring-[#00CBA9]/40'
              }`}
            >
              <div
                className={`px-4 py-3 border-b ${
                  isServiceMode
                    ? 'border-amber-300 bg-amber-500 text-white dark:border-amber-600 dark:bg-amber-600'
                    : 'border-[#00CBA9]/30 bg-[#00CBA9] text-white dark:border-[#00CBA9]/40'
                }`}
              >
                <h3 className="text-sm font-bold flex items-center gap-2">
                  {isServiceMode ? (
                    <Wrench key="icon-service" size={16} weight="bold" />
                  ) : (
                    <Clock key="icon-orders" size={16} weight="bold" />
                  )}
                  {isServiceMode ? t('planningSidebarServiceTitle') : t('planningSidebarOrdersTitle')}
                  <span
                    className={`ml-auto text-xs px-2 py-0.5 rounded-full font-bold ${
                      isServiceMode ? 'bg-white text-amber-700' : 'bg-white text-[#0f766e]'
                    }`}
                  >
                    {sidebarOrders.length}
                  </span>
                </h3>
              </div>
              <Droppable
                droppableId={isServiceMode ? 'sidebar-service' : 'sidebar-orders'}
                key={isServiceMode ? 'sidebar-service' : 'sidebar-orders'}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`p-3 flex-1 overflow-y-auto transition-colors ${
                      isServiceMode ? 'bg-amber-50/80 dark:bg-amber-950/30' : 'bg-[#f0fdfa] dark:bg-[#0f172a]'
                    } ${
                      snapshot.isDraggingOver
                        ? isServiceMode
                          ? 'bg-amber-200/60'
                          : 'bg-[#00CBA9]/10'
                        : ''
                    }`}
                    data-testid="unassigned-orders"
                  >
                    {sidebarOrders.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-[#94a3b8]">
                          {isServiceMode
                            ? t('planningNoSidebarService')
                            : t('planningNoSidebarMontage')}
                        </p>
                      </div>
                    ) : (
                      sidebarOrders.map((order, index) => (
                        <Draggable key={order.id} draggableId={String(order.id)} index={index}>
                          {(provided, snapshot) => (
                            <PortalAwareItem provided={provided} snapshot={snapshot}>
                              <div
                                className={`order-card mb-2 border-l-[4px] ${
                                  isServiceMode
                                    ? 'border-l-amber-500 bg-white dark:bg-[#1e293b]'
                                    : 'border-l-[#00CBA9] bg-white dark:bg-[#1e293b]'
                                }${snapshot.isDragging ? ' vmk-dragging' : ''}`}
                                data-testid={
                                  isServiceMode
                                    ? `service-planning-order-${order.id}`
                                    : `unassigned-order-${order.id}`
                                }
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <h4 className="font-semibold text-sm text-[#1a1a1a] dark:text-white">
                                    {order.client_name}
                                  </h4>
                                  <span
                                    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                      isServiceMode
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-[#00CBA9] text-white'
                                    }`}
                                  >
                                    {isServiceMode ? t('planningServiceTab') : t('planningOrdersTab')}
                                  </span>
                                </div>
                                <p className="text-xs text-[#64748b] flex items-center gap-1 mt-1">
                                  <MapPin
                                    size={12}
                                    className={isServiceMode ? 'text-amber-600' : 'text-[#00CBA9]'}
                                  />
                                  {order.address}
                                </p>
                                {isServiceMode ? (
                                  <p className="mt-1 text-[10px] font-medium text-amber-800 dark:text-amber-300">
                                    {t('planningServiceAwaiting')}
                                  </p>
                                ) : null}
                                {order.kozijnen && !isServiceMode ? (
                                  <p className="text-xs font-mono text-[#94a3b8] mt-2 bg-[#f8f9fb] dark:bg-[#1e293b] px-2 py-1 rounded">
                                    {order.kozijnen}
                                  </p>
                                ) : null}
                                <PlanningDurationControls
                                  orderId={order.id}
                                  duration={getSidebarDuration(order.id)}
                                  onChange={setSidebarDurationForOrder}
                                  t={t}
                                  testIdPrefix={isServiceMode ? 'planning-service' : 'planning'}
                                />
                              </div>
                            </PortalAwareItem>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>

          {/* Calendar Grid - Full Width */}
          <div
            key={`calendar-${planningMode}`}
            className={`flex-1 overflow-hidden rounded-xl ${
              isServiceMode
                ? 'ring-2 ring-amber-400/80 bg-amber-50/30 dark:ring-amber-500/50 dark:bg-amber-950/10'
                : 'ring-2 ring-[#00CBA9]/40 bg-white/50 dark:ring-[#00CBA9]/30 dark:bg-[#0f172a]/40'
            }`}
          >
            {viewMode === 'month' ? (
              <div className="h-full flex flex-col">
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {dayKeys.map((k) => (
                    <div key={k} className="text-xs font-semibold text-[#94a3b8] uppercase text-center py-1">
                      {t(k)}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2 flex-1 auto-rows-fr overflow-y-auto">
                  {monthGridDays.map((day) => {
                    const inCurrentMonth = day.getMonth() === startOfMonth(currentWeek).getMonth();
                    const isToday = isSameDay(day, new Date());
                    const dayOrders = getOrdersForDay(day);
                    return (
                      <div
                        key={format(day, 'yyyy-MM-dd')}
                        className={`rounded-xl border p-2 min-h-[130px] flex flex-col ${
                          inCurrentMonth
                            ? 'bg-white dark:bg-[#1e293b] border-[#e2e8f0] dark:border-[#334155]'
                            : 'bg-[#f8fafc] dark:bg-[#0f172a] border-[#e2e8f0] dark:border-[#1e293b] opacity-70'
                        }`}
                        data-testid={`month-day-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className={`text-sm font-bold ${
                              isToday ? 'text-[#00CBA9]' : 'text-[#1a1a1a] dark:text-white'
                            }`}
                          >
                            {format(day, 'd')}
                          </span>
                          <span className="text-[10px] text-[#94a3b8]">{dayOrders.length} {t('orders').toLowerCase()}</span>
                        </div>
                        <Droppable
                          droppableId={
                            isWeekend(day)
                              ? `month-weekend-${format(day, 'yyyy-MM-dd')}`
                              : `day-${format(day, 'yyyy-MM-dd')}-team-self`
                          }
                          isDropDisabled={isWeekend(day)}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`min-h-[72px] flex-1 space-y-1 overflow-y-auto rounded-md transition-colors ${
                                snapshot.isDraggingOver && !isWeekend(day) ? 'bg-[#00CBA9]/10' : ''
                              }`}
                            >
                              {dayOrders.slice(0, 4).map((order, index) => {
                                const team = teams.find((tm) => tm.id === order.assigned_team_id);
                                return (
                                  <Draggable
                                    key={planningDragId(order.id, day)}
                                    draggableId={planningDragId(order.id, day)}
                                    index={index}
                                  >
                                    {(dragProvided, dragSnapshot) => {
                                      const card = (
                                        <div
                                          ref={dragProvided.innerRef}
                                          {...dragProvided.draggableProps}
                                          style={dragProvided.draggableProps.style}
                                          className={`flex gap-1 ${dragSnapshot.isDragging ? 'opacity-90' : ''}`}
                                        >
                                          <div
                                            {...dragProvided.dragHandleProps}
                                            className="flex w-4 flex-shrink-0 cursor-grab items-center justify-center text-[#94a3b8]"
                                            aria-label="Drag to move"
                                          >
                                            ⋮
                                          </div>
                                          <button
                                            type="button"
                                            className="min-w-0 flex-1 text-left rounded-md border border-[#e2e8f0] dark:border-[#334155] px-2 py-1 bg-[#f8f9fb] dark:bg-[#0f172a] hover:brightness-[0.98] dark:hover:brightness-110 transition-[filter]"
                                            style={{
                                              backgroundColor: getOrderMarkerStyle(order)?.bg,
                                              borderLeftWidth: '3px',
                                              borderLeftColor:
                                                getOrderMarkerStyle(order)?.border || team?.color || '#00CBA9',
                                            }}
                                            onClick={() => navigate(getPlanningOrderViewPath(order))}
                                          >
                                            <div className="text-[11px] font-semibold truncate">{order.client_name}</div>
                                            <div className="text-[10px] text-[#64748b] truncate flex items-center gap-1">
                                              <span
                                                className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: team?.color || '#00CBA9' }}
                                              />
                                              <span className="truncate">
                                                {team?.name || t('team')} - {order.address}
                                              </span>
                                            </div>
                                          </button>
                                        </div>
                                      );
                                      return dragSnapshot.isDragging ? createPortal(card, document.body) : card;
                                    }}
                                  </Draggable>
                                );
                              })}
                              {dayOrders.length > 4 && (
                                <div className="text-[10px] text-[#64748b] px-1">
                                  +{dayOrders.length - 4} {t('more')}
                                </div>
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="h-full">
                <div className="grid grid-cols-7 gap-2 h-full w-full">
              {visibleDays.map((day, dayIndex) => {
                const isToday = isSameDay(day, new Date());
                const dayOrders = getOrdersForDay(day);
                const dayName = dayKeys[dayIndex];

                return (
                  <div 
                    key={dayIndex} 
                    className="planning-column flex flex-col h-full min-w-0" 
                    data-testid={`day-column-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <div className={`planning-column-header flex-shrink-0 ${isToday ? 'today' : ''}`}>
                      <div className={`text-xs font-medium ${isToday ? 'text-white/70' : 'text-[#94a3b8]'}`}>
                        {t(dayName) || format(day, 'EEE')}
                      </div>
                      <div className={`text-xl font-bold ${isToday ? 'text-white' : 'text-[#1a1a1a] dark:text-white'}`}>
                        {format(day, 'd')}
                      </div>
                      <div className={`text-xs ${isToday ? 'text-white/70' : 'text-[#94a3b8]'}`}>
                        {format(day, 'MMM')}
                      </div>
                    </div>
                    
                    {teams.length === 0 ? (
                      <div className="p-2 flex-1 text-center flex items-center justify-center">
                        <p className="text-xs text-[#94a3b8]">{t('addTeamsFirst')}</p>
                      </div>
                    ) : (
                      <div className="flex-1 overflow-y-auto divide-y divide-[#f1f5f9] dark:divide-[#1e293b]">
                        {teams.map((team) => {
                          const teamOrders = dayOrders.filter(o => o.assigned_team_id === team.id);
                          const droppableId = `day-${format(day, 'yyyy-MM-dd')}-team-${team.id}`;

                          return (
                            <Droppable key={team.id} droppableId={droppableId}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className={`p-2 min-h-[80px] transition-colors ${
                                    snapshot.isDraggingOver ? 'bg-[#00CBA9]/10' : ''
                                  }`}
                                  data-testid={`drop-zone-${format(day, 'yyyy-MM-dd')}-${team.id}`}
                                >
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <div
                                      className="w-2 h-2 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: team.color }}
                                    />
                                    <span className="text-[10px] font-semibold text-[#64748b] uppercase truncate">
                                      {team.name}
                                    </span>
                                  </div>
                                  {teamOrders.map((order, index) => (
                                    <Draggable
                                      key={planningDragId(order.id, day)}
                                      draggableId={planningDragId(order.id, day)}
                                      index={index}
                                    >
                                      {(provided, snapshot) => {
                                        const row = (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            style={provided.draggableProps.style}
                                            className={`mb-1.5 flex gap-1 ${
                                              snapshot.isDragging ? 'opacity-90' : ''
                                            }`}
                                            data-testid={`planned-order-${order.id}`}
                                          >
                                            <div
                                              {...provided.dragHandleProps}
                                              className="flex w-6 flex-shrink-0 cursor-grab items-center justify-center rounded-md border border-[#e2e8f0] bg-[#f8fafc] dark:border-[#334155] dark:bg-[#0f172a]"
                                              aria-label="Drag to move"
                                            >
                                              <span className="select-none text-[10px] leading-none text-[#94a3b8]">
                                                ⋮⋮
                                              </span>
                                            </div>
                                            <button
                                              type="button"
                                              className={`min-w-0 flex-1 cursor-pointer rounded-lg border border-[#e2e8f0] bg-white p-2 text-left dark:border-[#334155] dark:bg-[#1e293b] ${
                                                snapshot.isDragging
                                                  ? 'shadow-lg ring-2 ring-[#00CBA9]'
                                                  : ''
                                              }`}
                                              style={{
                                                backgroundColor: getOrderMarkerStyle(order)?.bg,
                                                borderLeftWidth: '3px',
                                                borderLeftColor: getOrderMarkerStyle(order)?.border || team.color,
                                              }}
                                              onClick={() => navigate(getPlanningOrderViewPath(order))}
                                            >
                                              <h4 className="truncate text-[11px] font-semibold text-[#1a1a1a] dark:text-white">
                                                {order.client_name}
                                              </h4>
                                              <p className="mt-0.5 truncate text-[10px] text-[#94a3b8]">
                                                {order.address}
                                                {(() => {
                                                  const label = formatPlanningDurationLabel(order, t);
                                                  return label ? ` · ${label}` : '';
                                                })()}
                                              </p>
                                            </button>
                                          </div>
                                        );
                                        return snapshot.isDragging
                                          ? createPortal(row, document.body)
                                          : row;
                                      }}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
                </div>
              </div>
            )}
          </div>
        </div>
      </DragDropContext>

      {teams.length === 0 && (
        <div className="mt-4 vmk-card bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-center">
          <p className="text-amber-700 dark:text-amber-400">
            {t('noTeamsConfigured')}
          </p>
        </div>
      )}
    </div>
  );
};

export default PlanningPage;
